import React, { useRef, useEffect } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import * as assetsAction from '../../../redux/actions/assetsAction';
import * as snackBarActions from '../../../redux/actions/snackBarActions';
import RemoveCircleIcon from '@material-ui/icons/RemoveCircle';
import { Box, Divider, Grid, IconButton, makeStyles, Menu, MenuItem, Typography } from '@material-ui/core';
import MoreHorizIcon from '@material-ui/icons/MoreHoriz';
import { useState } from 'react';
import ControlPointDuplicateIcon from '@material-ui/icons/ControlPointDuplicate';
import AssetsTempDrawer from '../AssetsTempDrawer';
import AssetNameTextFieldContainer from '../AssetNameTextField';
import EditIcon from '@material-ui/icons/Edit';
import AssetsHeaderWithCount from '../AssetsLabelCount';
import BackNextButtonContainer from '../BackNextButtonContainer';
import TabHeader from '../../common/TabHeader';
import ProductSearchWrapper from '../../Product/ProductSearchWrapper';
import BulkMultiTagSearch from './BulkMultiTagSearch';

//constants
const title = 'Bulk Add Details';
const explanation = 'Add information across all created assets using the fields found below.';

//Drawer assets
const removeAssetsTxt = 'Remove Asset';
const removeAssetConfirmationTxt = 'Are you sure you want to remove';

//Drawer remove bulk tags
const copyTxt = '-copy-';

//tags

const useStyles = makeStyles(() => ({
	tagsInputField: { minWidth: '24rem' },
	tempDrawerContainer: {
		width: '29em',
		paddingLeft: '2em',
		'& svg': { verticalAlign: 'middle' },
		'& button': {
			textTransform: 'none',
			width: '25em',
			marginTop: '2em',
		},
	},
	productSearchContainer: {
		width: '35em',
	},
}));

// function for getting next available name in the sequence.
// while duplicating an asset
const GetNextAvailableAssetName = (assets, assetName) => {
	let tempAssetName = assetName;
	const copyTxtIndex = assetName.indexOf(copyTxt);
	if (copyTxtIndex > -1) {
		tempAssetName = assetName.substring(0, copyTxtIndex);
	}
	let index = 1;
	let duplicateAssetName;
	do {
		duplicateAssetName = tempAssetName + copyTxt + index;
		index = index + 1;
	} while (IsAssetNameDuplicated(assets, duplicateAssetName));
	return duplicateAssetName;
};

//checking if current  asset name is duplicated.
const IsAssetNameDuplicated = (assets, assetName) => {
	return assets.some((asset) => asset.name === assetName);
};
//asset over flow menu
const RenderAssetOverFlowMenu = ({ asset, setAssetEditable }) => {
	const classes = useStyles();
	const dispatch = useDispatch();
	const store = useStore();
	const assets = store.getState().assets;
	//setting anchor element for display of overflow menu.
	const [anchorElement, setAnchorElement] = React.useState(null);
	const [displayDrawer, setDrawerDisplay] = React.useState(false);

	const open = Boolean(anchorElement);
	//Adding duplicate asset to redux store.
	const handleDuplicateMenuItemClick = (asset) => {
		const nextAvailableAssetName = GetNextAvailableAssetName(assets, asset.name);
		const duplicateAsset = {
			...asset,
			// getting id of the last asset and incrementing by 1 to form a new asset id.
			id: assets[assets.length - 1].id + 1,
			name: nextAvailableAssetName,
		};
		dispatch(assetsAction.addAssets([duplicateAsset]));
		setAnchorElement(null);
	};

	const handleRemoveMenuItemClick = () => {
		setDrawerDisplay(true);
		setAnchorElement(null);
	};

	//Setting asset editable state true on rename in  overflow menu.
	const handleRenameMenuItemClick = () => {
		setAssetEditable(true);
		setAnchorElement(null);
	};

	const RenderAssetRemoveTempDrawer = ({ open, setOpen }) => {
		//removing asset button in overflow menu
		const handleRemoveAssetBtnClick = async () => {
			try {
				await dispatch(assetsAction.removeAssets(asset.id));
				dispatch(snackBarActions.openSuccessSnackBar(asset.name + ' removed '));
			} catch (error) {
				dispatch(snackBarActions.openErrorSnackBar('Error removing asset' + asset.name));
			}
		};

		const tempDrawerProps = {
			open: open,
			setOpen: setOpen,
			handleRemoveBtnClick: handleRemoveAssetBtnClick,
			removeMsg: removeAssetsTxt,
			removeConfirmationMsg: removeAssetConfirmationTxt,
			assetName: asset.name + '?',
			removeBtnTxt: 'Yes, Remove Asset',
		};

		return <AssetsTempDrawer {...tempDrawerProps} />;
	};
	//overflow menu objectinfo mapping
	const assetOverFlowOptionsObj = [
		{
			name: ' Duplicate',
			icon: <ControlPointDuplicateIcon />,
			onClick: handleDuplicateMenuItemClick,
		},
		{
			name: ' Remove',
			icon: <RemoveCircleIcon />,
			onClick: handleRemoveMenuItemClick,
		},
		{
			name: ' Rename',
			icon: <EditIcon />,
			onClick: handleRenameMenuItemClick,
		},
	];

	const handleClick = (event) => {
		setAnchorElement(event.currentTarget);
	};

	const handleClose = () => {
		setAnchorElement(null);
	};
	return (
		<div className={classes.displayInlineBlock}>
			<IconButton aria-label="more" aria-controls="long-menu" aria-haspopup="true" onClick={handleClick}>
				<MoreHorizIcon />
			</IconButton>
			<Menu id="long-menu" anchorEl={anchorElement} keepMounted open={open} onClose={handleClose}>
				{assetOverFlowOptionsObj.map((option) => (
					<MenuItem key={option.name} onClick={() => option.onClick(asset)}>
						{option.icon} <span>{option.name}</span>
					</MenuItem>
				))}
			</Menu>
			{displayDrawer ? <RenderAssetRemoveTempDrawer open={displayDrawer} setOpen={setDrawerDisplay} /> : ''}
		</div>
	);
};

//toggle asset status expand/collapse
const RenderAsset = (asset) => {
	const classes = useStyles();
	const dispatch = useDispatch();
	const [isAssetEditable, setAssetEditable] = useState(false);
	const { product } = asset;
	const assetTxtField = useRef(null);
	const assetTxtFieldProps = {
		asset,
		setAssetEditable,
		isAssetEditable,
	};
	//setting focus to asset name text field when the rename option is clicked from the over flow menu.
	useEffect(() => {
		if (isAssetEditable) {
			assetTxtField.current.getElementsByTagName('input')[0].focus();
		}
	}, [isAssetEditable]);
	// handle product search element on value change.
	const productSearchOnValueChange = (selectedProductObj) => {
		dispatch(assetsAction.linkProductToAsset(asset, selectedProductObj));
	};
	const onInputChange = () => {
		dispatch(assetsAction.unlinkProductFromAsset(asset));
	};
	// product search components props
	const productSearchProps = {
		onValChange: productSearchOnValueChange,
		onInputChange: onInputChange,
		asset,
		product,
	};
	return (
		<Grid container direction="row" key={asset.id}>
			{isAssetEditable ? (
				<Grid item xs={5} className={classes.displayInlineBlock}>
					<AssetNameTextFieldContainer ref={assetTxtField} {...assetTxtFieldProps} />
				</Grid>
			) : (
				<Grid item>
					<Grid container alignItems="center">
						<Grid item>
							<Typography variant="body1" className={classes.displayInlineBlock}>
								{asset.name}
							</Typography>
						</Grid>
						<Grid item>
							<RenderAssetOverFlowMenu asset={asset} setAssetEditable={setAssetEditable} />
						</Grid>
					</Grid>
				</Grid>
			)}
			<Grid item xs={12}>
				<Grid container>
					<Typography>Product</Typography>
				</Grid>
				<Grid container className={classes.productSearchContainer}>
					<ProductSearchWrapper {...productSearchProps} />
				</Grid>
			</Grid>
		</Grid>
	);
};

const RenderAssets = () => {
	const assets = useSelector((state) => state.assets);
	return (
		<Grid container key="assets-container">
			{assets.map((asset) => {
				return (
					<Grid item xs={12} key={asset.id}>
						<RenderAsset {...asset} />
						<Box pt={2} />
						<Divider />
					</Grid>
				);
			})}
		</Grid>
	);
};

const AddAssetDetails = () => {
	const selectedTags = useSelector((state) => state.tags.selectedTags);
	const tagSearchProps = {
		assetTags: selectedTags,
	};
	const actions = [<BulkMultiTagSearch {...tagSearchProps} />];
	return (
		<Grid container direction="column" spacing={3} key="add-details-asset-container">
			<Grid item>
				<TabHeader
					title={title}
					explanation={explanation}
					explanationDataCy="bulk-details-content-msg"
					actions={actions}
				/>
			</Grid>
			<Grid item>
				<BackNextButtonContainer />
			</Grid>
			<Grid item>
				<AssetsHeaderWithCount />
			</Grid>
			<Grid item>
				<Divider />
			</Grid>
			<Grid item>
				<RenderAssets />
			</Grid>
		</Grid>
	);
};

export default AddAssetDetails;
